import { Search } from 'lucide-react';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Transition,
} from '@mantine/core';
import { useFetch } from '@mantine/hooks';
import Vimeo from '@u-wave/react-vimeo';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoCard from '../components/VideoCard.tsx';
import classes from './FreeApps.module.css';

export function FreeApps() {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');
  const [active, setActive] = useState<string>();
  const [value, setValue] = useState<string[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<any>();
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
  const [mountedCards, setMountedCards] = useState<number[]>([]);
  const { data, loading } = useFetch<{ data: any }>(
    `${import.meta.env.VITE_API_URL}/services/userVideos?category=2d`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  useEffect(() => {
    if (data) {
      data.data?.forEach((_: any, index: number) => {
        setTimeout(() => {
          setMountedCards(prev => [...prev, index]);
        }, 50 * index); // Add a delay of 50ms per card
      });
    }
  }, [data]);

  const groupedVideos: any = useMemo(() => {
    if (!data) return [];

    return Object.entries(
      data.data.reduce((groups: Record<string, any[]>, video: any) => {
        const category = video.sub_category || 'Uncategorized'; // Fallback for missing category
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(video);
        return groups;
      }, {})
    );
  }, [data]);

  const filteredVideos = useMemo(() => {
    if (value.length === 0 && !search) return groupedVideos;

    // Filter grouped videos
    return groupedVideos
      .map(([category, videos]: [string, any[]]) => {
        // Filter videos by search and category
        const filteredVideos = videos.filter((video: any) => {
          const matchesSearch = search
            ? video.name.toLowerCase().includes(search.toLowerCase())
            : true;
          const matchesCategory =
            value.length === 0 || value.includes(category);

          return matchesSearch && matchesCategory;
        });

        // Return the category and filtered videos
        return [category, filteredVideos];
      })
      .filter(([, videos]: any) => videos.length > 0); // Remove empty categories
  }, [groupedVideos, value, search]);

  // scroll behaviour
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  return (
    <>
      <Flex
        align={'center'}
        justify={'space-between'}
        direction={{ base: 'column', md: 'row' }}
        gap={'md'}
      >
        <Flex direction={'column'}>
          <Title flex={1}>
            {t('fa_free_apps', 'ESPERIENZE IMMERSIVE IN 2D')}
          </Title>
          <Title order={4} c={'dimmed'}>
            I video che puoi riprodurre in 2D per le tue sessioni da remoto
          </Title>
        </Flex>
        <TextInput
          value={search}
          w={'100%'}
          style={{ maxWidth: 450 }}
          placeholder={'Cerca video: es: Il Giardino Segreto'}
          rightSection={<Search size={16} />}
          onChange={v => setSearch(v.target.value)}
        />
      </Flex>
      <Divider mb={'md'} mt={'md'} />
      <div style={{ position: 'relative', minHeight: 150 }}>
        {loading ? (
          <Loader />
        ) : (
          // Render grouped videos by category
          <>
            <Checkbox.Group
              className={classes.checkbox}
              value={value}
              onChange={setValue}
            >
              <Flex
                direction={{ base: 'column', xs: 'row' }}
                wrap='wrap'
                gap={7}
              >
                {groupedVideos.map(([category]: any) => (
                  <Checkbox.Card radius='sm' value={category} key={category}>
                    <Flex align='center' gap='sm' justify='start' mr={10}>
                      <Checkbox.Indicator />
                      <Text style={{ whiteSpace: 'nowrap' }}>{category}</Text>
                    </Flex>
                  </Checkbox.Card>
                ))}
              </Flex>
            </Checkbox.Group>
            {filteredVideos.map(([category, videos]: any) => (
              <Stack mt={'lg'} mb={'sm'}>
                <Card shadow='sm' withBorder w='100%'>
                  <Group justify='space-between'>
                    <Title order={4}>{category}</Title>
                    {active === category ? (
                      <Button
                        variant={'outline'}
                        onClick={() => setActive(undefined)}
                      >
                        Nascondi
                      </Button>
                    ) : (
                      <Button
                        variant={'outline'}
                        onClick={() => setActive(category)}
                      >
                        Vedi tutti
                      </Button>
                    )}
                  </Group>
                </Card>
                <div style={{ position: 'relative' }}>
                  <Flex
                    ref={el => {
                      rowRefs.current[category] = el; // Assign ref directly
                    }}
                    gap={{ base: 5, xs: 'md', md: 'xl' }}
                    key={category}
                    wrap={active === category ? 'wrap' : 'nowrap'}
                    justify='start'
                    className={active === category ? '' : classes.scrollBar}
                    p={15}
                  >
                    {videos
                      .sort(
                        (a: any, b: any) =>
                          (a.has_product ? 0 : 1) - (b.has_product ? 0 : 1)
                      )
                      .map((video: any, index: number) => (
                        <Transition
                          key={video.id}
                          transition='fade-left'
                          duration={240}
                          timingFunction='ease'
                          onEnter={() => {}}
                          mounted={mountedCards.includes(index)}
                        >
                          {styles => (
                            <VideoCard
                              style={styles}
                              video={video}
                              setSelectedVideo={setSelectedVideo}
                            />
                          )}
                        </Transition>
                      ))}
                    {category.toLowerCase() === 'wellbeing' && (
                      <VideoCard
                        video={[572129372, 889641668, 699844408]}
                        title={
                          'Giardino Segreto - La Spiaggia al Tramonto - La Cascata nella Prateria'
                        }
                        image={'/loop.webp'}
                        setSelectedVideo={setSelectedVideo}
                      />
                    )}
                  </Flex>
                </div>
              </Stack>
            ))}
          </>
        )}
      </div>
      <Modal
        opened={selectedVideo !== undefined}
        onClose={() => setSelectedVideo(undefined)}
        title={selectedVideo?.title}
        centered
        size={'xl'}
        closeOnEscape
      >
        {selectedVideo ? (
          selectedVideo.s3_folder ? (
            <Vimeo
              video={selectedVideo.s3_folder}
              autoplay={false}
              showPortrait={false}
              showByline={false}
              showTitle={false}
              responsive={true}
              dnt={true}
            />
          ) : (
            <Vimeo
              video={selectedVideo[selectedVideoIndex]}
              autoplay={true}
              onEnd={() => {
                let i = selectedVideoIndex + 1;
                if (i >= selectedVideo.length) {
                  i = 0;
                }
                setSelectedVideoIndex(i);
              }}
              showPortrait={false}
              showByline={false}
              showTitle={false}
              responsive={true}
              dnt={true}
            />
          )
        ) : null}
        <Divider mt={12} mb={12} />
        <div>
          <Text>{selectedVideo?.short_description}</Text>
        </div>
      </Modal>
    </>
  );
}
